import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { renderHTML } from '@agility/nextjs';
import Link from 'next/link';
import { KitButton } from 'components/common/cms-kit';
import Box from '@mui/material/Box';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTheme } from '@mui/material/styles';
import Heading4 from 'components/common/subcomponents/Typography/Heading4';

import useMyLearning from '../../hooks/useMyLearning';
import { useSession } from 'next-auth/react';
import { isUserSubscriber } from '../../services/subscriptionUtils';
import { ALLOWED_ALIGNMENTS, CONSTANTS } from '../../services/constants';
import { useMediaQuery } from '@mui/material';

const VIMEO_PADDING_STYLE_IDENTIFIER = 'padding: 56.25%';

const TranscriptContainer = styled.div`
  width: 90%;
  margin-top: ${({ isSmallScreen }) => {
    return isSmallScreen ? '-70px' : '-15px';
  }};
  margin-bottom: 35px;
  margin-left: 35px;
  display: flex;
  flex-direction: column;
  align-items: ${({ alignment }) => {
    if (alignment === CONSTANTS.ALIGNMENT.LEFT) {
      return 'flex-start';
    }

    if (alignment === CONSTANTS.ALIGNMENT.RIGHT) {
      return 'flex-end';
    }

    return CONSTANTS.ALIGNMENT.CENTER;
  }};
`;

const TranscriptToggle = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ textColor }) => textColor};
  font-weight: 700;
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 4px;
`;

const TranscriptText = styled.div`
  width: 100%;
  margin-top: 10px;
  color: ${({ textColor }) => textColor};
  text-align: left;
`;

const VideoTranscript = ({
  alignment,
  isSmallScreen,
  transcriptText,
  textColor,
}) => {
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const TranscriptIcon = isTranscriptOpen ? ExpandLessIcon : ExpandMoreIcon;

  return (
    <TranscriptContainer alignment={alignment} isSmallScreen={isSmallScreen}>
      <TranscriptToggle
        type="button"
        textColor={textColor}
        onClick={() => setIsTranscriptOpen((current) => !current)}
      >
        {isTranscriptOpen ? 'Close Video Transcript' : 'Read Video Transcript'}
        <TranscriptIcon fontSize="small" />
      </TranscriptToggle>
      {isTranscriptOpen && (
        <TranscriptText
          textColor={textColor}
          className="prose max-w-full"
          dangerouslySetInnerHTML={renderHTML(transcriptText)}
        />
      )}
    </TranscriptContainer>
  );
};

const Container = styled('div')(({ $grayBackground }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor:
    $grayBackground === 'true'
      ? 'rgba(217, 217, 217, 0.3)'
      : 'rgba(255, 255, 255, 1)',
  backgroundSize: '100%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10% bottom 75%',
  position: 'relative',
  '@media screen and (max-width: 899px)': {
    minHeight: '180px',
  },
  '@media screen and (min-width: 900px)': {
    minHeight: '420px',
  },
}));

const DescriptionBox = styled('div')({
  margin: '35px 20px 0 20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  '@media screen and (min-width: 992px)': {
    maxWidth: '550px',
  },
  '@media screen and (max-width: 992px)': {
    maxWidth: '95%',
  },
});

const VideoContainer = styled('div')({
  flexGrow: 0,
  flexShrink: 1,
  margin: '35px 0',
  '.video': {
    aspectRatio: '16 / 9',
    width: '600px',
  },
});

const Video = ({ module }) => {
  const { fields } = module;
  const theme = useTheme();
  const isMyLearning = useMyLearning();

  const [buttonText, setButtonText] = useState(fields?.buttonText);
  const [buttonLink, setButtonLink] = useState(fields.buttonLink);
  const { data: session } = useSession();
  const isSmallScreen = useMediaQuery('(max-width:767px)');

  useEffect(() => {
    if (session) {
      if (isUserSubscriber(session, CONSTANTS.PROGRAMS.WMS)) {
        setButtonText(fields.buttonLabelforSubscribedUser || buttonText);
        setButtonLink(fields.buttonLinkforSubscriber || buttonLink);
      }
    }
  }, [session]);

  if (isMyLearning) {
    return;
  }

  const EmbedContainer = styled('div')(({ alignment }) => {
    let justifyContent = CONSTANTS.ALIGNMENT.CENTER;
    const iframePositionStyles = {
      left: '50% !important',
      transform: 'translateX(-50%)',
    };
    let directIframeMargin = '0 auto';

    if (alignment === CONSTANTS.ALIGNMENT.LEFT) {
      justifyContent = 'flex-start';
      iframePositionStyles.left = '0 !important';
      iframePositionStyles.transform = 'none';
      directIframeMargin = '0 auto 0 0';
    } else if (alignment === CONSTANTS.ALIGNMENT.RIGHT) {
      justifyContent = 'flex-end';
      iframePositionStyles.left = 'auto !important';
      iframePositionStyles.right = '0 !important';
      iframePositionStyles.transform = 'none';
      directIframeMargin = '0 0 0 auto';
    }

    return {
      '@media screen and (min-width: 600px)': {
        width: '400px',
      },
      '@media screen and (min-width: 768px)': {
        width: '550px',
      },
      '@media screen and (min-width: 992px)': {
        width: '700px',
      },
      '@media screen and (min-width: 993px)': {
        width: '900px',
      },
      display: 'flex',
      justifyContent,
      alignItems: 'center',
      width: '100%',
      [`& > div[style*='${VIMEO_PADDING_STYLE_IDENTIFIER}'] > iframe`]: {
        ...iframePositionStyles,
      },
      '& > iframe': {
        display: 'block',
        margin: directIframeMargin,
      },
      [`& > div[style*='${VIMEO_PADDING_STYLE_IDENTIFIER}']`]: {
        width: '100%',
        height: '100%',
      },
      '& > p': {
        width: '100%',
        textAlign: alignment,
      },
    };
  });

  const showVideoTranscript =
    fields?.showVideoTranscript === true ||
    fields?.showVideoTranscript === 'true';
  const transcriptText =
    typeof fields?.videoTranscript === 'string'
      ? fields.videoTranscript
      : fields?.videoTranscript?.text ||
        fields?.videoTranscript?.transcript?.text ||
        '';
  const hasTranscript = transcriptText.trim().length > 0;

  const validatedAlignment = ALLOWED_ALIGNMENTS.includes(fields.videoAlignment)
    ? fields.videoAlignment
    : CONSTANTS.ALIGNMENT.CENTER;

  return (
    <React.Fragment>
      <Container $grayBackground={fields.grayBackground}>
        {fields.description && (
          <DescriptionBox>
            <Heading4 color="#000">{fields.description}</Heading4>
          </DescriptionBox>
        )}
        <Box
          sx={{
            width: '100%',
            ml: 'auto',
            mr: 'auto',
            maxWidth: {
              sm: '540px',
              md: '768px',
              lg: '1180px',
              xl: '1180px',
            },
          }}
        >
          <VideoContainer>
            <EmbedContainer
              className="prose max-w-full mx-auto"
              alignment={validatedAlignment}
              dangerouslySetInnerHTML={renderHTML(
                fields.videoEmbed.replace(/<p[^>]*>|<\/p>/g, ''),
              )}
            />
          </VideoContainer>
          {showVideoTranscript && hasTranscript && (
            <VideoTranscript
              alignment={validatedAlignment}
              isSmallScreen={isSmallScreen}
              transcriptText={transcriptText}
              textColor={theme.palette.primary.dark}
            />
          )}
        </Box>
        {fields.showButton === 'true' && (
          <>
            <Link href={buttonLink.href}>
              <KitButton
                round
                size="lg"
                style={{
                  backgroundColor: theme.palette.primary.light,
                  marginBottom: '50px',
                }}
              >
                <a
                  href={buttonLink.href}
                  target={buttonLink.target}
                  rel="noopener noreferrer"
                >
                  {buttonText}
                </a>
              </KitButton>
            </Link>
          </>
        )}
      </Container>
    </React.Fragment>
  );
};

export default Video;
